import { z } from "zod";
import { DEFAULT_LAYOUTS } from "@dashboard/components/DashboardWidgets/constants/layouts";
import {
    DEFAULT_ACTIVE_WIDGETS,
    STORAGE_KEYS,
} from "@dashboard/components/DashboardWidgets/constants/dashboardConfig";
import { normalizeWidgetId } from "@dashboard/components/DashboardWidgets/constants/widgets";
import type { DashboardProfileConfig, ResponsiveLayouts } from "@dashboard/utils/dashboard.types";

const LEGACY_GROUPED_WIDGET_ID = "grouped-widget";
const LEGACY_STAT_WIDGET_IDS = [
    "ATTENTION_TASKS",
    "YOUR_RISKS",
    "OVERDUE_ISSUES",
    "YOUR_ISSUES",
    "YOUR_IMPROVEMENTS",
] as const;

const LayoutItemSchema = z
    .object({
        i: z.string().min(1),
        x: z.number().int().nonnegative(),
        y: z.number().int().nonnegative(),
        w: z.number().positive(),
        h: z.number().positive(),
    })
    .passthrough();

const LayoutsSchema = z.record(z.string(), z.array(LayoutItemSchema));
const ActiveWidgetsSchema = z.array(z.string().min(1));

export function cloneDashboardProfileConfig(
    config: DashboardProfileConfig,
): DashboardProfileConfig {
    return structuredClone(config);
}

export function createDefaultDashboardProfileConfig(): DashboardProfileConfig {
    return cloneDashboardProfileConfig({
        layouts: DEFAULT_LAYOUTS,
        activeWidgets: DEFAULT_ACTIVE_WIDGETS,
    });
}

function parseStoredValue(key: string): unknown {
    const value = globalThis.localStorage?.getItem(key);
    if (value === null || value === undefined) return undefined;
    try {
        return JSON.parse(value) as unknown;
    } catch {
        return undefined;
    }
}

function migrateLayouts(value: unknown): ResponsiveLayouts {
    const result = LayoutsSchema.safeParse(value);
    if (!result.success) return structuredClone(DEFAULT_LAYOUTS);

    const containsGroupedWidget = Object.values(result.data).some((layout) =>
        layout.some((item) => item.i === LEGACY_GROUPED_WIDGET_ID),
    );
    return containsGroupedWidget
        ? structuredClone(DEFAULT_LAYOUTS)
        : normalizeLayoutWidgetIds(result.data as ResponsiveLayouts);
}

function migrateWidgets(value: unknown): string[] {
    const result = ActiveWidgetsSchema.safeParse(value);
    if (!result.success) return [...DEFAULT_ACTIVE_WIDGETS];

    const hasLegacyWidget = result.data.includes(LEGACY_GROUPED_WIDGET_ID);
    const normalizedWidgets = normalizeWidgetIds(
        result.data.filter((id) => id !== LEGACY_GROUPED_WIDGET_ID),
    );
    const isMissingStats = LEGACY_STAT_WIDGET_IDS.some((id) => !normalizedWidgets.includes(id));
    if (!hasLegacyWidget || !isMissingStats) return normalizedWidgets;

    return normalizeWidgetIds([...LEGACY_STAT_WIDGET_IDS, ...normalizedWidgets]);
}

function normalizeWidgetIds(widgetIds: string[]): string[] {
    return [...new Set(widgetIds.map(normalizeWidgetId))];
}

function normalizeLayoutWidgetIds(layouts: ResponsiveLayouts): ResponsiveLayouts {
    return Object.fromEntries(
        Object.entries(layouts).map(([breakpoint, layout]) => {
            const widgetIds = new Set<string>();
            const normalizedLayout = (layout ?? [])
                .map((item) => ({ ...item, i: normalizeWidgetId(item.i) }))
                .filter((item) => {
                    if (widgetIds.has(item.i)) return false;
                    widgetIds.add(item.i);
                    return true;
                });

            return [breakpoint, normalizedLayout];
        }),
    );
}

export function getInitialDashboardProfileConfig(): DashboardProfileConfig {
    return {
        layouts: migrateLayouts(parseStoredValue(STORAGE_KEYS.LAYOUTS)),
        activeWidgets: migrateWidgets(parseStoredValue(STORAGE_KEYS.WIDGETS)),
    };
}

export function getLegacyDashboardProfileConfig(): DashboardProfileConfig | undefined {
    const layouts = parseStoredValue(STORAGE_KEYS.LAYOUTS);
    const activeWidgets = parseStoredValue(STORAGE_KEYS.WIDGETS);

    if (layouts === undefined && activeWidgets === undefined) return undefined;

    return {
        layouts: migrateLayouts(layouts),
        activeWidgets: migrateWidgets(activeWidgets),
    };
}

export function removeLegacyDashboardProfileConfig(): void {
    globalThis.localStorage?.removeItem(STORAGE_KEYS.LAYOUTS);
    globalThis.localStorage?.removeItem(STORAGE_KEYS.WIDGETS);
}

export function getRenderedDashboardWidgetIds(
    activeWidgets: string[],
    availableWidgetIds: ReadonlySet<string>,
): string[] {
    return activeWidgets.filter((id) => availableWidgetIds.has(id));
}
